import React, { useState, useEffect, useMemo, useRef } from "react";
import api from "../services/api";
import {
  Container,
  Button,
  Form,
  Table,
  Alert,
  Card,
  InputGroup,
  FormControl,
  Row,
  Col,
  Spinner,
  Dropdown,
  Badge,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
  Modal,
  ButtonGroup,
  ToggleButton,
} from "react-bootstrap";
import {
  FaDownload,
  FaUpload,
  FaSyncAlt,
  FaSearch,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaFileAlt,
  FaUser,
  FaCalendarAlt,
  FaCloudUploadAlt,
  FaSortAmountDown,
  FaSortAmountUp,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileArchive,
  FaInfoCircle,
  FaHistory,
  FaEye,
  FaTags,
  FaTrashAlt,
  FaFileVideo,
  FaFileAudio,
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "./DocumentManager.css";

const PAGE_SIZES = [5, 10, 20, 50];
const PAGINATION_POSITION = "top";

/* ---------- File-type helpers ---------- */
const FILE_ICON_BY_EXT = {
  pdf: FaFilePdf,
  doc: FaFileWord,
  docx: FaFileWord,
  xls: FaFileExcel,
  xlsx: FaFileExcel,
  csv: FaFileExcel,
  ppt: FaFilePowerpoint,
  pptx: FaFilePowerpoint,
  png: FaFileImage,
  jpg: FaFileImage,
  jpeg: FaFileImage,
  gif: FaFileImage,
  webp: FaFileImage,
  svg: FaFileImage,
  zip: FaFileArchive,
  rar: FaFileArchive,
  mp4: FaFileVideo,
  mov: FaFileVideo,
  avi: FaFileVideo,
  webm: FaFileVideo,
  mp3: FaFileAudio,
  wav: FaFileAudio,
  ogg: FaFileAudio,
};

const getFileExt = (title) => {
  if (!title) return "";
  const parts = String(title).split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const FileTypeIcon = ({ title, className = "" }) => {
  const ext = getFileExt(title);
  const Icon = FILE_ICON_BY_EXT[ext] || FaFileAlt;
  return <Icon className={className} />;
};

/* ---------- Preview configuration ---------- */
const MAX_INLINE_TEXT_BYTES = 300_000; // ~300 KB
const MAX_EXCEL_ROWS = 50;
const MAX_EXCEL_COLS = 20;

const EXCEL_MIMES = new Set([
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const inferMimeFromExt = (title) => {
  const ext = getFileExt(title);
  const map = {
    pdf: "application/pdf",
    txt: "text/plain",
    csv: "text/csv",
    json: "application/json",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    svg: "image/svg+xml",
    webp: "image/webp",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    mp4: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
  };
  return map[ext] || "";
};

/** Build a usable, absolute URL. External viewers need this. */
const buildFileUrl = (doc) => {
  if (doc?.file_url) return doc.file_url;
  const path = `/docs/documents/${doc.id}/download/`;
  const apiBaseUrl = api?.defaults?.baseURL;

  // If the API base URL is an absolute URL, use it to construct the final URL.
  if (apiBaseUrl && (apiBaseUrl.startsWith("http://") || apiBaseUrl.startsWith("https://"))) {
    return new URL(path, apiBaseUrl).href;
  }
  
  // Otherwise, assume the API is on the same host and construct the URL from the window's origin.
  return new URL(path, window.location.origin).href;
};

/** Decide preview kind from MIME and size. */
const getPreviewKind = (doc) => {
  const mime = doc.content_type || inferMimeFromExt(doc.title);
  const size = doc.file_size || 0;

  if (!doc.id && !doc.file_url) return { kind: "none", reason: "no-url" };

  if (mime.startsWith("image/")) return { kind: "image" };
  if (mime.startsWith("video/")) return { kind: "video" };
  if (mime.startsWith("audio/")) return { kind: "audio" };
  if (mime === "application/pdf") return { kind: "pdf" };
  if (EXCEL_MIMES.has(mime)) return { kind: "excel" };
  if (
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  ) {
    return { kind: "ppt" };
  }
  if (mime.startsWith("text/") || mime === "application/json") {
    if (size > MAX_INLINE_TEXT_BYTES) return { kind: "too-large-text" };
    return { kind: "text" };
  }
  return { kind: "unsupported" };
};

/* ---------- Reusable pagination ---------- */
const PaginationBar = ({ page, pageSize, total, totalPages, onPrev, onNext, position = "bottom" }) => (
  <div className={`pagination-container p-3 ${position === "top" ? "border-bottom" : "border-top"}`}>
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <div className="pagination-info fw-medium text-muted">
        Showing <strong>{(page - 1) * pageSize + 1}</strong> – <strong>{Math.min(page * pageSize, total)}</strong> of{" "}
        <strong>{total}</strong> | Page <strong>{page}</strong> of <strong>{totalPages}</strong>
      </div>
      <div className="d-flex gap-2 align-items-center">
        <Button variant="outline-primary" onClick={onPrev} disabled={page === 1} className="pagination-btn" aria-label="Previous page">
          <FaChevronLeft />
        </Button>
        <Button
          variant="outline-primary"
          onClick={onNext}
          disabled={page === totalPages}
          className="pagination-btn"
          aria-label="Next page"
        >
          <FaChevronRight />
        </Button>
      </div>
    </div>
  </div>
);

const DocumentManager = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [perFileProgress, setPerFileProgress] = useState({});
  const [uploading, setUploading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // search / sort
  const [rawQuery, setRawQuery] = useState("");
  const [query, setQuery] = useState("");
  const [sortNewFirst, setSortNewFirst] = useState(true);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);

  // upload section collapsed by default
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // preview
  const [previewDoc, setPreviewDoc] = useState(null);
  const [textPreview, setTextPreview] = useState({ loading: false, content: "", error: "" });
  const [excelPreview, setExcelPreview] = useState({ loading: false, html: "", error: "" });

  // ppt state
  const [ppt, setPpt] = useState({
    loading: false,
    publicUrl: "",
    error: "",
    viewer: "google", // "office" | "google"
  });

  const dropRef = useRef(null);
  const fileInputRef = useRef(null);
  const debounceTimer = useRef(null);

  /* Debounce search */
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setQuery(rawQuery.trim()), 250);
    return () => clearTimeout(debounceTimer.current);
  }, [rawQuery]);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get("/docs/documents/");
      setDocuments(response.data || []);
      setError("");
    } catch {
      setError("Failed to fetch documents.");
    } finally {
      setLoading(false);
    }
  };

  /* Filter + sort */
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    let list = documents.filter((d) => {
      if (!q) return true;
      const title = (d.title || "").toLowerCase();
      const uploader = (d.uploader_name || "").toLowerCase();
      return title.includes(q) || uploader.includes(q);
    });

    list.sort((a, b) => {
      const ta = new Date(a.uploaded_at).getTime();
      const tb = new Date(b.uploaded_at).getTime();
      return sortNewFirst ? tb - ta : ta - tb;
    });
    return list;
  }, [documents, query, sortNewFirst]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  /* Drag & drop for upload */
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDragOver = (e) => {
      e.preventDefault();
      setIsDraggingOver(true);
      if (!uploadOpen) setUploadOpen(true);
    };
    const onDragLeave = (e) => {
      if (!el.contains(e.relatedTarget)) setIsDraggingOver(false);
    };
    const onDrop = (e) => {
      e.preventDefault();
      setIsDraggingOver(false);
      const filesList = Array.from(e.dataTransfer.files || []);
      if (filesList.length) {
        setSelectedFiles((prev) => [...prev, ...filesList]);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [uploadOpen]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "N/A";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.min(3, Math.floor(Math.log(Math.max(bytes, 1)) / Math.log(1024)));
    const val = Math.round((bytes / Math.pow(1024, i)) * 100) / 100;
    return `${val} ${sizes[i]}`;
  };

  /* Upload handlers */
  const handleFileSelect = (e) => {
    const filesList = Array.from(e.target.files || []);
    if (filesList.length) setSelectedFiles((prev) => [...prev, ...filesList]);
  };

  const resetUploadState = () => {
    setSelectedFiles([]);
    setPerFileProgress({});
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async (e) => {
    e?.preventDefault();
    setError("");
    setSuccess("");
    if (!selectedFiles.length) {
      setError("Please select one or more files.");
      return;
    }

    try {
      setUploading(true);
      const progressMap = {};
      const filesArr = [...selectedFiles];

      for (let i = 0; i < filesArr.length; i += 1) {
        const f = filesArr[i];
        const formData = new FormData();
        formData.append("file", f);
        formData.append("title", f.name);

        await api.post("/docs/documents/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (pe) => {
            const pct = Math.round((pe.loaded * 100) / (pe.total || 1));
            progressMap[f.name] = pct;
            setPerFileProgress({ ...progressMap });
          },
        });
      }

      setSuccess(filesArr.length === 1 ? "Document uploaded successfully." : `${filesArr.length} documents uploaded successfully.`);
      resetUploadState();
      fetchDocuments();
    } catch (err) {
      setError("Upload failed: " + (err?.response?.data?.detail || err?.message || "Unknown error"));
    } finally {
      setUploading(false);
    }
  };

  /* Download */
  const handleDownload = async (id, filename) => {
    setError("");
    setSuccess("");
    try {
      const response = await api.get(`/docs/documents/${id}/download/`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSuccess("Download started.");
    } catch (err) {
      setError("Download failed: " + (err?.message || "Unknown error"));
    }
  };

  /* Replace */
  const handleReplace = async (id, newFile) => {
    if (!newFile) {
      setError("Please select a file to replace.");
      return;
    }
    setError("");
    setSuccess("");
    const formData = new FormData();
    formData.append("file", newFile);
    try {
      await api.post(`/docs/documents/${id}/replace/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess("Document replaced successfully.");
      fetchDocuments();
    } catch (err) {
      setError("Replacement failed: " + (err?.message || "Unknown error"));
    }
  };

  const handleReplaceFileChange = (id, e) => {
    const newFile = e.target.files?.[0];
    if (newFile) handleReplace(id, newFile);
  };

  /* Preview orchestrator */
  const openPreview = async (doc) => {
    setPreviewDoc(doc);
    setTextPreview({ loading: false, content: "", error: "" });
    setExcelPreview({ loading: false, html: "", error: "" });
    setPpt({ loading: false, publicUrl: "", error: "", viewer: "google" });

    const { kind } = getPreviewKind(doc);

    if (kind === "text") {
      try {
        setTextPreview({ loading: true, content: "", error: "" });
        const { data } = await api.get(`/docs/documents/${doc.id}/download/`, {
          responseType: "text",
          transformResponse: [(v) => v],
        });
        setTextPreview({ loading: false, content: data, error: "" });
      } catch {
        setTextPreview({ loading: false, content: "", error: "Unable to load text preview." });
      }
    }

    if (kind === "excel") {
      try {
        setExcelPreview({ loading: true, html: "", error: "" });
        const XLSXmod = await import("xlsx");
        const XLSX = XLSXmod.default || XLSXmod;
        const { data: ab } = await api.get(`/docs/documents/${doc.id}/download/`, {
          responseType: "arraybuffer",
        });
        const wb = XLSX.read(ab, { type: "array" });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        const fullRef = ws["!ref"] || "A1:A1";
        const rng = XLSX.utils.decode_range(fullRef);
        rng.e.r = Math.min(rng.e.r, MAX_EXCEL_ROWS - 1);
        rng.e.c = Math.min(rng.e.c, MAX_EXCEL_COLS - 1);
        const refLimited = XLSX.utils.encode_range(rng);
        const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, range: refLimited, blankrows: false });
        const escape = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
        const table = [
          '<table class="excel-preview-table">',
          ...aoa.map((row) => `<tr>${row.map((cell) => `<td>${escape(cell)}</td>`).join("")}</tr>`),
          "</table>",
        ].join("");
        setExcelPreview({ loading: false, html: table, error: "" });
      } catch {
        setExcelPreview({ loading: false, html: "", error: "Unable to render Excel preview." });
      }
    }

    if (kind === "ppt") {
      try {
        const publicUrl = buildFileUrl(doc); // Use the direct URL builder
        setPpt({ loading: false, publicUrl, error: "", viewer: "google" });
      } catch (e) {
        setPpt({ loading: false, publicUrl: "", error: e?.message || "Unable to obtain preview URL.", viewer: "google" });
      }
    }
  };

  const removeSelectedFile = (name) => setSelectedFiles((files) => files.filter((f) => f.name !== name));
  const clearSelectedFiles = () => setSelectedFiles([]);

  const overallProgress = useMemo(() => {
    const names = selectedFiles.map((f) => f.name);
    if (!names.length) return 0;
    const sum = names.reduce((acc, n) => acc + (perFileProgress[n] || 0), 0);
    return Math.round(sum / names.length);
  }, [selectedFiles, perFileProgress]);

  /* ---------- Render ---------- */
  const SkeletonRows = ({ rows = pageSize }) => (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={`sk-${i}`} className="document-row">
          <td>
            <div className="skeleton skeleton-title w-75 mb-2" />
            <div className="skeleton skeleton-sub w-25" />
          </td>
          <td>
            <div className="skeleton skeleton-sub w-50" />
          </td>
          <td>
            <div className="skeleton skeleton-sub w-50" />
          </td>
          <td>
            <div className="d-flex justify-content-center gap-2">
              <div className="skeleton skeleton-chip" />
              <div className="skeleton skeleton-chip" />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <Container className="mt-4 document-manager-container" ref={dropRef}>
      {/* Header */}
      <div className="dm-header-bar mb-3">
        <div className="d-flex align-items-center header-block">
          <div className="document-manager-icon me-3">
            <FaFileAlt />
          </div>
          <div>
            <h1 className="document-manager-header mb-0">Documents & Checklists</h1>
            <p className="document-manager-subtitle text-muted mb-0">
              {filtered.length} document{filtered.length !== 1 ? "s" : ""} available
            </p>
          </div>
        </div>

        <div className="dm-header-actions">
          <InputGroup className="dm-search-group">
            <InputGroup.Text className="search-icon">
              <FaSearch />
            </InputGroup.Text>
            <FormControl
              placeholder="Search documents..."
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              aria-label="Search documents"
              className="search-input"
            />
            {rawQuery && (
              <Button variant="outline-secondary" onClick={() => setRawQuery("")} aria-label="Clear search" className="clear-search-btn">
                <FaTimes />
              </Button>
            )}
          </InputGroup>

          <div className="d-flex gap-2 align-items-center">
            <Dropdown>
              <Dropdown.Toggle variant="outline-primary" id="sort-dropdown" className="sort-toggle">
                {sortNewFirst ? <FaSortAmountDown /> : <FaSortAmountUp />}
                <span className="d-none d-md-inline ms-1">{sortNewFirst ? "Newest" : "Oldest"}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSortNewFirst(true)}>
                  <FaSortAmountDown className="me-2" /> Newest First
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortNewFirst(false)}>
                  <FaSortAmountUp className="me-2" /> Oldest First
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <OverlayTrigger placement="bottom" overlay={<Tooltip>Refresh list</Tooltip>}>
              <Button variant="outline-secondary" onClick={fetchDocuments} disabled={loading} className="refresh-btn">
                <FaSyncAlt className={loading ? "spinning" : ""} />
              </Button>
            </OverlayTrigger>

            <Button
              variant={uploadOpen ? "primary" : "outline-primary"}
              onClick={() => setUploadOpen((o) => !o)}
              className="dm-upload-toggle-btn"
            >
              <FaCloudUploadAlt className="me-1" />
              <span className="d-none d-sm-inline">Upload</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible className="alert-custom">
          <strong>Error:</strong> {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess("")} dismissible className="alert-custom">
          <strong>Success:</strong> {success}
        </Alert>
      )}

      {/* Upload - collapsible */}
      <div className={`dm-upload-panel ${uploadOpen ? "dm-upload-panel--open" : ""}`}>
        <Card className="mb-3 upload-card shadow-sm">
          <Card.Body className="p-3">
            <Form onSubmit={handleUpload}>
              <div
                className={`dm-drop-zone ${isDraggingOver ? "dm-drop-zone--active" : ""} ${selectedFiles.length ? "dm-drop-zone--has-files" : ""}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="d-none"
                  multiple
                />
                <FaCloudUploadAlt className="dm-drop-zone-icon" />
                <div className="dm-drop-zone-text">
                  <strong>Drop files here</strong> or <span className="dm-drop-zone-link">browse</span>
                </div>
                <div className="dm-drop-zone-hint">All file types supported</div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="dm-selected-files mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <Badge bg="light" text="dark" className="file-badge">
                      {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""} ready
                    </Badge>
                    <Button size="sm" variant="link" className="text-muted p-0" onClick={clearSelectedFiles}>
                      <FaTimes className="me-1" />Clear
                    </Button>
                  </div>
                  <div className="selected-file-list">
                    {selectedFiles.slice(0, 5).map((f) => (
                      <div key={f.name} className="selected-file-chip">
                        <FileTypeIcon title={f.name} className="me-1" />
                        <span className="truncate">{f.name}</span>
                        <button type="button" className="chip-remove" onClick={(e) => { e.stopPropagation(); removeSelectedFile(f.name); }} aria-label={`Remove ${f.name}`}>
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                    {selectedFiles.length > 5 && <span className="ms-2 text-muted small">+{selectedFiles.length - 5} more</span>}
                  </div>

                  {uploading && (
                    <div className="mt-2">
                      <ProgressBar now={overallProgress} label={`${overallProgress}%`} animated />
                    </div>
                  )}

                  <Button type="submit" variant="primary" disabled={uploading} className="upload-btn mt-3 w-100">
                    {uploading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Uploading {overallProgress}%
                      </>
                    ) : (
                      <>
                        <FaUpload className="me-2" />
                        Upload {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </Form>
          </Card.Body>
        </Card>
      </div>

      {/* Documents */}
      <Card className="shadow-sm documents-card">
        <Card.Header className="documents-card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <FaFileAlt className="me-2" />
              <span>Available Documents</span>
              <Badge bg="primary" className="ms-2 document-count">
                {filtered.length}
              </Badge>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                aria-label="Rows per page"
                className="page-size-select"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s} per page
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {!loading && filtered.length > 0 && (PAGINATION_POSITION === "top" || PAGINATION_POSITION === "both") && (
            <PaginationBar
              position="top"
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          )}

          <div className="table-container">
            <Table hover className="documents-table mb-0">
              <thead className="table-header">
                <tr>
                  <th width="40%">Document</th>
                  <th width="20%">
                    <FaCalendarAlt className="me-1" />
                    Uploaded
                  </th>
                  <th width="20%">
                    <FaUser className="me-1" />
                    Uploaded By
                  </th>
                  <th width="20%" className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows />
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-5 empty-state">
                      <FaFileAlt className="display-4 text-muted mb-3" />
                      <h5 className="text-muted">No documents found</h5>
                      <p className="text-muted mb-0">{query ? "Try adjusting your search terms" : "Upload your first document to get started"}</p>
                    </td>
                  </tr>
                ) : (
                  paged.map((doc) => (
                    <tr key={doc.id} className="document-row">
                      <td>
                        <div className="document-info">
                          <FileTypeIcon title={doc.title} className="document-icon me-2" />
                          <div>
                            <div className="document-title">{doc.title}</div>
                            <div className="d-flex align-items-center gap-2">
                              {doc.file_size && <small className="text-muted">{formatFileSize(doc.file_size)}</small>}
                              {doc.tags?.length ? (
                                <small className="text-muted d-inline-flex align-items-center">
                                  <FaTags className="me-1" />
                                  {doc.tags.slice(0, 2).join(", ")}
                                  {doc.tags.length > 2 ? "…" : ""}
                                </small>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="upload-time">
                          {new Date(doc.uploaded_at).toLocaleDateString()}
                          <br />
                          <small className="text-muted">{new Date(doc.uploaded_at).toLocaleTimeString()}</small>
                        </div>
                      </td>
                      <td>
                        <div className="uploader-info d-flex align-items-center">
                          <div className="avatar-circle me-2">{(doc.uploader_name || "U").slice(0, 1).toUpperCase()}</div>
                          {doc.uploader_name || "Unknown"}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex justify-content-center gap-2 actions-container">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Preview</Tooltip>}>
                            <Button variant="outline-secondary" size="sm" className="action-btn" onClick={() => openPreview(doc)} aria-label={`Preview ${doc.title}`}>
                              <FaEye />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Download</Tooltip>}>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleDownload(doc.id, doc.title)}
                              className="action-btn"
                              aria-label={`Download ${doc.title}`}
                            >
                              <FaDownload />
                            </Button>
                          </OverlayTrigger>
                          <div className="replace-container">
                            <Form.Group controlId={`replaceFile-${doc.id}`} className="m-0">
                              <Form.Control type="file" onChange={(e) => handleReplaceFileChange(doc.id, e)} className="replace-file-input" title={`Replace ${doc.title}`} />
                              <OverlayTrigger placement="top" overlay={<Tooltip>Replace</Tooltip>}>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => document.getElementById(`replaceFile-${doc.id}`)?.click()}
                                  className="action-btn"
                                  aria-label={`Replace ${doc.title}`}
                                >
                                  <FaHistory />
                                </Button>
                              </OverlayTrigger>
                            </Form.Group>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {!loading && paged.length > 0 && (PAGINATION_POSITION === "bottom" || PAGINATION_POSITION === "both") && (
            <PaginationBar
              position="bottom"
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          )}
        </Card.Body>
      </Card>

      {/* Preview Modal */}
      <Modal show={!!previewDoc} onHide={() => setPreviewDoc(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FileTypeIcon title={previewDoc?.title} className="me-2" />
            {previewDoc?.title || "Preview"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {(() => {
            if (!previewDoc) return null;
            const { kind } = getPreviewKind(previewDoc);
            const url = buildFileUrl(previewDoc);

            if (kind === "pdf") {
              return (
                <div className="pdf-embed-container">
                  <embed src={url} type="application/pdf" className="w-100" height="600" />
                </div>
              );
            }

            if (kind === "image") {
              return (
                <div className="d-flex justify-content-center">
                  <img src={url} alt={previewDoc.title} style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 8 }} />
                </div>
              );
            }

            if (kind === "video") {
              return (
                <div className="d-flex justify-content-center">
                  <video
                    src={url}
                    controls
                    controlsList="nodownload"
                    crossOrigin="use-credentials"
                    style={{ maxWidth: "100%", maxHeight: 600, borderRadius: 8 }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              );
            }

            if (kind === "audio") {
              return (
                <div className="d-flex justify-content-center p-3">
                  <audio src={url} controls className="w-100" crossOrigin="use-credentials">
                    Your browser does not support the audio element.
                  </audio>
                </div>
              );
            }

            if (kind === "ppt") {
              if (ppt.loading) {
                return (
                  <div className="text-muted d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" /> Preparing preview link…
                  </div>
                );
              }
              if (ppt.error) {
                return <div className="text-danger">{ppt.error}</div>;
              }
              if (!ppt.publicUrl) {
                return <div className="text-muted">No preview link available. Download the file instead.</div>;
              }

              const officeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(ppt.publicUrl)}`;
              const googleSrc = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(ppt.publicUrl)}`;
              const iframeSrc = ppt.viewer === "office" ? officeSrc : googleSrc;

              return (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <ButtonGroup>
                      <ToggleButton
                        id="ppt-office"
                        type="radio"
                        variant={ppt.viewer === "office" ? "primary" : "outline-primary"}
                        checked={ppt.viewer === "office"}
                        value="office"
                        onChange={() => setPpt((p) => ({ ...p, viewer: "office" }))}
                      >
                        Office Viewer
                      </ToggleButton>
                      <ToggleButton
                        id="ppt-google"
                        type="radio"
                        variant={ppt.viewer === "google" ? "primary" : "outline-primary"}
                        checked={ppt.viewer === "google"}
                        value="google"
                        onChange={() => setPpt((p) => ({ ...p, viewer: "google" }))}
                      >
                        Google Viewer
                      </ToggleButton>
                    </ButtonGroup>
                    <Button size="sm" variant="outline-secondary" onClick={() => window.open(ppt.publicUrl, "_blank")}>
                      Open original in new tab
                    </Button>
                  </div>
                  <iframe title="PowerPoint Preview" src={iframeSrc} frameBorder="0" className="w-100" height="600" allowFullScreen />
                  <div className="text-muted small mt-2">
                    If the viewer shows an error, the public link may be blocked by your domain policy, file size, or the URL might have expired.
                  </div>
                </>
              );
            }

            if (kind === "text") {
              if (textPreview.loading) {
                return (
                  <div className="text-muted d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" /> Loading preview…
                  </div>
                );
              }
              if (textPreview.error) return <div className="text-danger">{textPreview.error}</div>;
              return <pre className="text-preview-pane">{String(textPreview.content || "").slice(0, MAX_INLINE_TEXT_BYTES)}</pre>;
            }

            if (kind === "excel") {
              if (excelPreview.loading) {
                return (
                  <div className="text-muted d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" /> Rendering preview…
                  </div>
                );
              }
              if (excelPreview.error) return <div className="text-danger">{excelPreview.error}</div>;
              if (!excelPreview.html) return <div className="text-muted">No content detected in the first sheet.</div>;
              return <div className="excel-preview-wrap" dangerouslySetInnerHTML={{ __html: excelPreview.html }} />;
            }

            if (kind === "too-large-text") {
              return <div className="text-muted">This text file is too large to preview inline. Use Download to open it locally.</div>;
            }

            if (kind === "unsupported") {
              return <div className="text-muted">Preview is not available for this file type. Use Download to open the document.</div>;
            }

            return <div className="text-muted">File preview URL is not available. Use Download to view the document locally.</div>;
          })()}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPreviewDoc(null)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (previewDoc) handleDownload(previewDoc.id, previewDoc.title);
            }}
          >
            <FaDownload className="me-2" />
            Download
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DocumentManager;